import React from "react";
import { Button } from "@/components/ui/button";
import { useChangelog } from "@/contexts/ChangelogContext";
import { cn } from "@/lib/utils";
import { AlertCircle, ArrowUpRight, Loader2, PlayCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getChangelogPreviewUrl } from "@/services/changelogMediaService";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type ChangeCategory = "added" | "fixed" | "improved";

const CHANGE_TYPE_STYLES: Record<ChangeCategory, string> = {
  added:
    "border-emerald-200 bg-emerald-50/80 text-emerald-800 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-200",
  fixed:
    "border-rose-200 bg-rose-50/80 text-rose-800 dark:border-rose-400/30 dark:bg-rose-500/10 dark:text-rose-200",
  improved:
    "border-sky-200 bg-sky-50/80 text-sky-800 dark:border-sky-400/30 dark:bg-sky-500/10 dark:text-sky-200",
};

const CHANGE_TYPE_LABELS: Record<ChangeCategory, string> = {
  added: "Nuevo",
  fixed: "Corregido",
  improved: "Mejorado",
};

const DATE_FORMATTER = new Intl.DateTimeFormat("es-ES", {
  year: "numeric",
  month: "long",
  day: "numeric",
});

const formatDate = (isoDate: string) => {
  try {
    return DATE_FORMATTER.format(new Date(isoDate));
  } catch (error) {
    return isoDate;
  }
};

type PreviewState = {
  open: boolean;
  path: string | null;
  url: string | null;
  loading: boolean;
  error: string | null;
};

const createInitialPreviewState = (): PreviewState => ({
  open: false,
  path: null,
  url: null,
  loading: false,
  error: null,
});

export const ChangelogModal: React.FC = () => {
  const navigate = useNavigate();
  // Shared state and actions exposed by the changelog context.
  const {
    isOpen,
    loading,
    error,
    manualMode,
    visibleChanges,
    currentVersion,
    closeChangelog,
    acknowledgeCurrentVersion,
  } = useChangelog();

  const showEmptyState = !loading && !error && visibleChanges.length === 0;
  const showChangeList = !loading && !error && visibleChanges.length > 0;
  const showErrorState = !loading && Boolean(error);



  const handleConfirm = () => {
    acknowledgeCurrentVersion();
  };

  const handleNavigateToPage = useCallback(
    (rawPath?: string) => {
      const path = `${rawPath ?? ""}`.trim();
      if (!path) {
        return;
      }

      closeChangelog();
      navigate(path);
    },
    [closeChangelog, navigate]
  );

  const [previewState, setPreviewState] = useState<PreviewState>(createInitialPreviewState);
  const [previewCache, setPreviewCache] = useState<Record<string, string>>({});
  const [previewImageDimensions, setPreviewImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const sanitizePath = useCallback((rawPath: string) => {
    const trimmed = `${rawPath ?? ""}`.trim();
    if (!trimmed) {
      return "";
    }

    return trimmed.replace(/^\/+/, "");
  }, []);

  const handlePreviewOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setPreviewState(createInitialPreviewState());
      setPreviewImageDimensions(null);
    }
  }, []);

  const handleOpenPreview = useCallback(
    async (rawPath: string) => {
      const normalizedPath = sanitizePath(rawPath);
      if (!normalizedPath) {
        setPreviewState({
          open: true,
          path: null,
          url: null,
          loading: false,
          error: "La ruta del recurso multimedia no es válida.",
        });
        return;
      }

      const cachedUrl = previewCache[normalizedPath] ?? null;
      setPreviewState({
        open: true,
        path: normalizedPath,
        url: cachedUrl,
        loading: !cachedUrl,
        error: null,
      });
      setPreviewImageDimensions(null);

      if (cachedUrl) {
        return;
      }

      try {
        const signedUrl = await getChangelogPreviewUrl(normalizedPath);
        if (!isMountedRef.current) {
          return;
        }
        setPreviewCache((previous) => ({
          ...previous,
          [normalizedPath]: signedUrl,
        }));
        setPreviewState((previous) => {
          if (previous.path !== normalizedPath) {
            return previous;
          }
          return {
            ...previous,
            loading: false,
            url: signedUrl,
            error: null,
          };
        });
        setPreviewImageDimensions(null);
      } catch (error) {
        if (!isMountedRef.current) {
          return;
        }
        const message =
          error instanceof Error
            ? error.message || "No se pudo cargar la vista previa."
            : "No se pudo cargar la vista previa.";
        setPreviewState((previous) => {
          if (previous.path !== normalizedPath) {
            return previous;
          }
          return {
            ...previous,
            loading: false,
            error: message,
          };
        });
        setPreviewImageDimensions(null);
      }
    },
    [previewCache, sanitizePath]
  );

  useEffect(() => {
    if (!previewState.url) {
      setPreviewImageDimensions(null);
    }
  }, [previewState.url]);

  const previewImageAspectRatio = useMemo(() => {
    if (!previewImageDimensions || previewImageDimensions.height === 0) {
      return null;
    }

    return previewImageDimensions.width / previewImageDimensions.height;
  }, [previewImageDimensions]);

  const handlePreviewImageLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      const { naturalWidth, naturalHeight } = event.currentTarget;

      if (naturalWidth > 0 && naturalHeight > 0) {
        setPreviewImageDimensions({ width: naturalWidth, height: naturalHeight });
      }
    },
    []
  );

  const previewContent = useMemo(() => {
    if (!previewState.open) {
      return null;
    }

    if (previewState.loading) {
      return (
        <div className="flex flex-1 items-center justify-center gap-2 text-sm text-slate-600 dark:text-slate-200">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Cargando vista previa…
        </div>
      );
    }

    if (previewState.error) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 text-center text-sm text-rose-600 dark:text-rose-200">
          <AlertCircle className="h-6 w-6" aria-hidden="true" />
          <p>{previewState.error}</p>
        </div>
      );
    }

    if (previewState.url) {
      const imageWrapperStyle = previewImageAspectRatio
        ? { aspectRatio: previewImageAspectRatio, maxHeight: '70vh' }
        : { maxHeight: '70vh' };

      return (
        <div className="flex flex-1 items-center justify-center bg-slate-900/5 p-4 dark:bg-slate-900/40">
          <div
            className="flex w-full max-w-full items-center justify-center rounded-md border border-border bg-card/70 p-2 shadow-surface dark:bg-card/50"
            style={imageWrapperStyle}
          >
            <img
              src={previewState.url}
              alt="Vista previa animada de la actualización"
              className="h-full w-full object-contain"
              onLoad={handlePreviewImageLoad}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-1 items-center justify-center px-4 text-sm text-slate-600 dark:text-slate-300">
        No hay contenido disponible para esta vista previa.
      </div>
    );
  }, [handlePreviewImageLoad, previewImageAspectRatio, previewState]);  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) closeChangelog(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-0 flex flex-col rounded-none">
        <DialogHeader className="px-6 pt-6 relative z-10">
          <DialogTitle className="font-serif text-3xl font-light text-primary dark:text-white">
            ¿Qué hay de nuevo?
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600 dark:text-slate-300">
            {manualMode
              ? "Revisa las novedades que aún no has visitado."
              : "Estas son las novedades desde tu última visita."}
          </DialogDescription>
          {currentVersion && (
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400 mt-1">
              Versión actual: {currentVersion}
            </p>
          )}
        </DialogHeader>

        <div className="relative z-10 flex flex-1 flex-col gap-4 px-6 pb-6 pt-4 overlay-scrollable overflow-y-auto">
          {/* Loading state shown while the changelog data is requested. */}
          {loading && (
            <div className="flex flex-1 items-center justify-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Cargando novedades…
            </div>
          )}

          {/* Error message when the JSON cannot be retrieved. */}
          {showErrorState && (
            <div className="flex items-start gap-3 border border-rose-200/70 bg-rose-50/70 p-4 text-sm text-rose-900 dark:border-rose-400/30 dark:bg-rose-500/10 dark:text-rose-100">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <p>
                No pudimos cargar el changelog en este momento. Inténtalo de nuevo más tarde.
              </p>
            </div>
          )}

          {/* Friendly empty state when there are no new versions to list. */}
          {showEmptyState && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 border border-border bg-card/70 p-6 text-center dark:bg-card/60">
              <p className="text-base font-semibold text-primary dark:text-slate-100">¡Estás al día!</p>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                No hay cambios pendientes de revisar por el momento.
              </p>
            </div>
          )}

          {/* Grouped list of versions and their associated entries. */}
          {showChangeList && (
            <div className="space-y-4">
              {visibleChanges.map((entry) => (
                <section
                  key={entry.version}
                  className="group border border-border bg-card/80 p-4 shadow-surface transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-surface-hover dark:bg-card/70"
                  aria-labelledby={`version-${entry.version}`}
                >
                  <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <h3
                        id={`version-${entry.version}`}
                        className="text-lg font-semibold text-primary dark:text-white"
                      >
                        v{entry.version}
                      </h3>
                      <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        {visibleChanges.length > 1 ? "Incluido en esta actualización" : "Cambios incluidos"}
                      </p>
                    </div>
                    <div className="flex flex-col items-start gap-2 md:items-end md:text-right">
                      <time
                        dateTime={entry.date}
                        className="text-sm font-medium text-slate-600 dark:text-slate-300"
                      >
                        {formatDate(entry.date)}
                      </time>
                    </div>
                  </header>
                  <ul className="mt-4 space-y-2" role="list">
                    {entry.changes.map((change, index) => {
                      const changeCategory = change.type as ChangeCategory;
                      const badgeClass =
                        CHANGE_TYPE_STYLES[changeCategory] ?? CHANGE_TYPE_STYLES.added;
                      const changeLabel =
                        CHANGE_TYPE_LABELS[changeCategory] ?? CHANGE_TYPE_LABELS.added;
                      return (
                        <li
                          key={`${entry.version}-${change.type}-${index}`}
                          className="flex flex-col gap-3 border border-border bg-card/90 p-3 text-sm text-slate-700 shadow-surface transition-colors dark:bg-card/70 dark:text-slate-200 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                        >
                          <div className="flex flex-1 items-start gap-3">
                            <span
                              className={cn(
                                "inline-flex h-6 items-center justify-center border px-2 text-[11px] font-semibold uppercase tracking-wide",
                                badgeClass
                              )}
                            >
                              {changeLabel}
                            </span>
                            <p className="leading-relaxed">{change.description}</p>
                          </div>
                          {(change.demoGifPath || change.page) && (
                            <div className="flex shrink-0 items-center gap-2 sm:justify-end">
                              {change.demoGifPath && (
                                <Button
                                  type="button"
                                  size="icon"
                                  variant="outline"
                                  className="border-primary/50 text-primary hover:bg-primary/10 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-800/60"
                                  onClick={() => handleOpenPreview(change.demoGifPath!)}
                                  aria-label="Ver demostración"
                                  title="Ver demostración"
                                >
                                  <PlayCircle className="h-5 w-5" aria-hidden="true" />
                                  <span className="sr-only">Ver demostración</span>
                                </Button>
                              )}
                              {change.page && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="border-primary/40 text-primary hover:bg-primary/10 dark:border-slate-500 dark:text-slate-100 dark:hover:bg-slate-800/60"
                                  onClick={() => handleNavigateToPage(change.page)}
                                  aria-label="Abrir página relacionada"
                                  title="Abrir página relacionada"
                                >
                                  <ArrowUpRight className="h-5 w-5" aria-hidden="true" />
                                  <span className="sr-only">Abrir página relacionada</span>
                                </Button>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-border bg-card/80 px-6 py-4 flex justify-end gap-2 relative z-10 shrink-0">
          <Button
            type="button"
            variant="ghost"
            onClick={closeChangelog}
            className="text-slate-600 hover:text-slate-800 dark:text-slate-300 dark:hover:text-white"
          >
            Cerrar
          </Button>
          <Button type="button" onClick={handleConfirm}>
            Entendido
          </Button>
        </div>

        {previewState.open && (
          <div className="mt-4 border-t pt-4 px-6 pb-6 relative z-10">
            <h4 className="font-serif text-lg font-medium text-primary">Vista previa de la novedad</h4>
            <div className="mt-2">{previewContent}</div>
            <div className="mt-3 flex justify-end">
              <Button type="button" variant="ghost" onClick={() => handlePreviewOpenChange(false)}>
                Cerrar vista previa
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
